import {type PopoverContentProps} from "@radix-ui/react-popover";
import {ChevronsUpDown} from "lucide-react";
import * as RPNInput from "react-phone-number-input";

import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import {Button} from "@/components/ui/button";
import CountrySelectList from "@/components/ui/CountrySelectList";
import FlagComponent from "@/components/ui/FlagComponent";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import useCountries from "@/hooks/useCountries";
import clsxm from "@/lib/clsxm";
import {type ReactNode, useState} from "react";
import {useEvent, useMeasure} from "react-use";

type CountrySelectProps = {
  align?: PopoverContentProps["align"];
  buttonClassName?: string;
  chevronClassName?: string;
  chevronIcon?: boolean;
  countryNameClassName?: string;
  disabled?: boolean;
  error?: string;
  fitListToContent?: boolean;
  flagAndLabelClassName?: string;
  hidePhoneNumber?: boolean;
  hideSearch?: boolean;
  itemClassName?: string;
  listClassName?: string;
  onChange: (country: RPNInput.Country) => void;
  onlyCountries?: readonly RPNInput.Country[];
  prioritizedCountries?: readonly RPNInput.Country[];
  readOnly?: boolean;
  renderItemAccessory?: (country: RPNInput.Country) => ReactNode;
  renderValueAccessory?: ReactNode;
  selectedItemClassName?: string;
  showCountryName?: boolean;
  value: RPNInput.Country;
};

const CountrySelect = ({
  align,
  buttonClassName,
  chevronClassName,
  chevronIcon,
  countryNameClassName,
  disabled,
  error,
  fitListToContent,
  flagAndLabelClassName,
  hidePhoneNumber,
  hideSearch,
  itemClassName,
  listClassName,
  onChange,
  onlyCountries,
  prioritizedCountries,
  readOnly,
  renderItemAccessory,
  renderValueAccessory,
  selectedItemClassName,
  showCountryName,
  value: selectedCountry,
}: CountrySelectProps) => {
  const [open, setOpen] = useState(false);
  const {getCountryTranslatedLabelByCode} = useCountries();
  const [buttonContainerRef, {width: buttonWidth}] = useMeasure<HTMLDivElement>();

  const DropdownIcon = chevronIcon ? RightChevronIcon : ChevronsUpDown;

  useEvent("scroll", () => setOpen(false));

  return (
    <Popover open={open} onOpenChange={next => !readOnly && !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <div ref={buttonContainerRef} className={clsxm(disabled && "cursor-not-allowed")}>
          <Button
            type="button"
            variant="outline"
            className={clsxm(
              "flex h-14 w-25 gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10",
              "disabled:bg-neutral-white-dark disabled:text-neutral-grey-dark disabled:opacity-100",
              error && "border-danger-medium-red focus:border-danger-dark-red",
              buttonClassName,
            )}
            disabled={disabled}>
            <div className={clsxm("contents", flagAndLabelClassName)}>
              <FlagComponent
                className="shrink-0"
                country={selectedCountry ?? "CH"}
                countryName={selectedCountry ?? "CH"}
              />
              {showCountryName && (
                <span className={clsxm("min-w-0 truncate text-base font-medium", countryNameClassName)}>
                  {getCountryTranslatedLabelByCode(selectedCountry ?? "CH")}
                </span>
              )}
            </div>
            {renderValueAccessory}
            <DropdownIcon
              className={clsxm(
                "-mr-2 size-4 opacity-50 transition-transform",
                chevronIcon && (open ? "-rotate-90" : "rotate-90"),
                disabled || readOnly ? "hidden" : "opacity-100",
                chevronClassName,
              )}
            />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent align={align} className="rounded-lg p-0" wrapperClassName="w-full">
        <CountrySelectList
          fitListToContent={fitListToContent}
          hidePhoneNumber={hidePhoneNumber}
          hideSearch={hideSearch}
          itemClassName={itemClassName}
          listClassName={listClassName}
          onChange={country => {
            onChange(country);
            setOpen(false);
          }}
          onlyCountries={onlyCountries}
          prioritizedCountries={prioritizedCountries}
          renderItemAccessory={renderItemAccessory}
          selectedItemClassName={selectedItemClassName}
          value={selectedCountry}
          width={buttonWidth}
        />
      </PopoverContent>
    </Popover>
  );
};

export default CountrySelect;
